// PowerBIHelpSection.tsx
import React from "react";
import {
  Card,
  Stack,
  Title,
  Text,
  List,
  Code,
  Group,
  Button,
  Tooltip,
  CopyButton,
  Divider,
  Alert,
} from "@mantine/core";

const CopyBtn: React.FC<{ value: string; label?: string }> = ({ value, label = "Kopier" }) => (
  <CopyButton value={value} timeout={1200}>
    {({ copied, copy }) => (
      <Tooltip label={copied ? "Kopiert!" : label} withinPortal>
        <Button
          variant="secondary"
          size="sm"
          onClick={copy}
          leftSection={<i className="fa fa-copy fa-fw" />}
          style={{ minWidth: 110 }}
        >
          {copied ? "Kopiert" : label}
        </Button>
      </Tooltip>
    )}
  </CopyButton>
);

const BlockRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack gap={6} mt="xs">
    <Text size="sm" c="dimmed">
      {label}
    </Text>
    <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
      <Code block style={{ flex: 1, overflowX: "auto", whiteSpace: "pre" }}>
        {value}
      </Code>
      <CopyBtn value={value} />
    </Group>
  </Stack>
);

const PowerBIHelpSection: React.FC<{ url: string }> = ({ url }) => {
  return (
    <Card withBorder radius="lg" shadow="sm">
      <Stack gap="md">
        <Group gap="xs">
          <i className="fa fa-plug fa-fw" />
          <Title order={5} m={0}>
            Power BI – 3 enkle steg
          </Title>
        </Group>

        <Alert variant="light" color="blue" icon={<i className="fa fa-info-circle" />}>
          <Text size="sm" fw={600}>
            Erstatt <Code>DIN_API_KEY</Code> i URL-en med hele din personlige API-nøkkel. Bruk nøkkelen du kopierte da
            den ble opprettet, ikke den korte nøkkel-ID-en (prefikset).
          </Text>
        </Alert>

        <List size="sm" withPadding spacing="xs">
          <List.Item>
            <b>1.</b> Åpne <b>Power BI Desktop</b> → <b>Hent data</b> → <b>Web</b>
          </List.Item>
          <List.Item>
            <b>2.</b> Lim inn denne URL:en:
            <BlockRow label="Komplett URL" value={url} />
          </List.Item>
          <List.Item>
            <b>3.</b> Klikk <b>OK</b> → velg <b>Anonym (Anonymous)</b> → <b>Koble til</b>. API-nøkkelen sendes allerede
            som <Code>apiKey</Code> i URL-en. Ikke velg organisasjonskonto eller legg til en Bearer-header.
          </List.Item>
        </List>

        <Text size="sm">
          Har du allerede valgt organisasjonskonto? Åpne{" "}
          <b>Fil → Alternativer og innstillinger → Datakildeinnstillinger</b>, velg den aktuelle MCApps-datakilden og
          fjern de lagrede tillatelsene. Koble til på nytt med <b>Anonym</b>.
        </Text>

        <Divider />

        <Alert variant="light" color="green" icon={<i className="fa fa-check-circle" />}>
          <Text size="sm">
            Når tilkoblingen er opprettet, kan du bearbeide JSON-dataene i Power Query og laste dem inn i rapporten.
          </Text>
        </Alert>

        <Alert variant="light" color="yellow" icon={<i className="fa fa-lock" />}>
          <Text size="sm">
            <b>Personlig nøkkel:</b> Hele nøkkelen lagres i datakildens URL i Power BI. Behandle URL-en som en
            hemmelighet. Ved rotasjon må URL-en oppdateres med den nye nøkkelen.
          </Text>
        </Alert>
      </Stack>
    </Card>
  );
};

export default PowerBIHelpSection;
